/**
 * Fog of war: when territories come out of heavy fog (you conquer next to
 * them, say), the fog fades off them instead of vanishing in one frame
 * (docs/fog-of-war/README.md).
 *
 * It remembers which territories were in heavy fog; on each change, those no
 * longer fogged get a copy of the fog (their shape on filled maps, their disc
 * on marker maps) in an overlay <Svg> above the troop numbers, which fades
 * out by native view opacity, revealing the colours and numbers underneath.
 * Nothing animates on first load, when fog thickens, or under Reduce Motion.
 */
import React from 'react'
import { Circle, Defs, Path } from 'react-native-svg'
import { Easing, useAnimatedStyle, useReducedMotion, useSharedValue, withSequence, withTiming } from 'react-native-reanimated'
import { HEAVY_FOG_FILL, HEAVY_FOG_LINE, liftedTerritoryIds } from '../../lib/fogStyle'
import { FogHatchPattern } from './FogHatchPattern'
import { MapOverlayLayer, type MapOverlayFrame } from './MapOverlayLayer'

/** What to draw for one lifted territory: its shape, or a disc. */
export type FogLiftShape = { id: string; d: string } | { id: string; cx: number; cy: number; r: number; strokeWidth: number }

/** Total time a lift stays mounted: hold, fade, and a little slack. */
const LIFT_MS = 80 + 600 + 80

export function FogLiftLayer({ fogged, shapeOf, frame, patternId, gap }: {
  /** Ids currently in heavy fog (owner unknown). */
  fogged: ReadonlySet<string>
  shapeOf: (territoryId: string) => FogLiftShape | null
  frame: MapOverlayFrame
  patternId: string
  gap: number
}) {
  const reducedMotion = useReducedMotion()
  const previous = React.useRef(fogged)
  const nextKey = React.useRef(0)
  const [lifts, setLifts] = React.useState<Array<{ key: number; shapes: FogLiftShape[] }>>([])

  React.useEffect(() => {
    const before = previous.current
    previous.current = fogged
    if (reducedMotion || before === fogged) return
    const shapes = liftedTerritoryIds(before, fogged).map(shapeOf).filter((s): s is FogLiftShape => s !== null)
    if (shapes.length === 0) return
    const key = nextKey.current++
    setLifts((current) => [...current, { key, shapes }])
  // shapeOf is read only when the fogged set changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fogged, reducedMotion])

  return <>
    {lifts.map(({ key, shapes }) => (
      <FogLift
        key={key}
        frame={frame}
        shapes={shapes}
        patternId={`${patternId}-${key}`}
        gap={gap}
        onDone={() => setLifts((current) => current.filter((lift) => lift.key !== key))}
      />
    ))}
  </>
}

function FogLift({ frame, shapes, patternId, gap, onDone }: {
  frame: MapOverlayFrame
  shapes: FogLiftShape[]
  patternId: string
  gap: number
  onDone: () => void
}) {
  const opacity = useSharedValue(1)
  React.useEffect(() => {
    opacity.value = withSequence(
      withTiming(1, { duration: 80 }),
      withTiming(0, { duration: 600, easing: Easing.out(Easing.quad) }),
    )
    const handle = setTimeout(onDone, LIFT_MS)
    return () => clearTimeout(handle)
  // Runs once: each lift fades and removes itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const style = useAnimatedStyle(() => ({ opacity: opacity.value }))
  const fill = `url(#${patternId})`
  return (
    <MapOverlayLayer
      {...frame}
      style={style}
      defs={<Defs><FogHatchPattern id={patternId} gap={gap} background={HEAVY_FOG_FILL} color={HEAVY_FOG_LINE} /></Defs>}
    >
      {shapes.map((shape) => 'd' in shape
        ? <Path key={shape.id} d={shape.d} fill={fill} pointerEvents="none" />
        : <Circle key={shape.id} cx={shape.cx} cy={shape.cy} r={shape.r} fill={fill} stroke="#171717" strokeWidth={shape.strokeWidth} pointerEvents="none" />)}
    </MapOverlayLayer>
  )
}
